import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { StripePaymentForm } from "@/components/billing/StripePaymentForm";
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { TrackLocations, useSegmentTrack } from "@/hooks/use-segment";
import { useStripePromise } from "@/hooks/use-stripe";
import { useCompleteBillingSetup, useReloadBillingInfo, useSetupBilling } from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import { Elements } from "@stripe/react-stripe-js";
import type { PaymentIntent, StripeError } from "@stripe/stripe-js";
import type { FC } from "react";
import { useCallback, useState } from "react";

type StripePaymentButtonProps = {
    children: (state: { isLoading: boolean; onAddPayment: () => void; showForm: boolean }) => React.ReactNode;
    submitButtonText?: string;
    dialogTitle?: string;
    "data-tracking-id": string;
    onSuccess?: () => void;
};

export const StripePaymentButton: FC<StripePaymentButtonProps> = ({
    children: renderButton,
    onSuccess,
    submitButtonText: _submitButtonText,
    dialogTitle = "Verify your payment details",
    "data-tracking-id": dataTrackingId,
}) => {
    const setupBilling = useSetupBilling();
    const completeBillingSetup = useCompleteBillingSetup();
    const { toast } = useToast();
    const track = useSegmentTrack();

    const { reload: reloadBillingInfo } = useReloadBillingInfo();

    const [showPaymentForm, setShowPaymentForm] = useState<{ clientSecret: string } | undefined>();
    const [isCompletingBillingSetup, setIsCompletingBillingSetup] = useState(false);

    const handleAddPaymentMethod = useCallback(async () => {
        try {
            const { setupIntentClientSecret } = await setupBilling.mutateAsync();
            setShowPaymentForm({
                clientSecret: setupIntentClientSecret,
            });
        } catch (e) {
            console.error(e);
            toast({
                title: "Oh no, there was a problem with our payment service.",
                description: formatError(e),
            });
        }
    }, [setupBilling, toast]);

    const handleSuccess = useCallback(
        async (paymentIntent: PaymentIntent) => {
            try {
                setIsCompletingBillingSetup(true);
                const { trialAmountCredits } = await completeBillingSetup.mutateAsync(paymentIntent.id);
                toast({
                    title: "Billing setup completed successfully!",
                    description: `You have ${trialAmountCredits} free trial OCUs.`,
                });
                reloadBillingInfo();
                setShowPaymentForm(undefined);

                if (onSuccess) {
                    onSuccess();
                }
            } catch (error) {
                toast({
                    title: "Sorry, there was a problem completing your billing setup.",
                    description: formatError(error),
                });
            }
        },
        [completeBillingSetup, reloadBillingInfo, toast, onSuccess],
    );

    const handleModalOpenChange = useCallback((open: boolean) => {
        if (!open) {
            setShowPaymentForm(undefined);
        }
    }, []);

    const handleError = useCallback(
        (error: StripeError | Error) => {
            track("verification_failed", {
                step: "stripe_payment",
                error: formatError(error),
            });
            toast({
                title: "Failed to complete verification",
                description: formatError(error),
            });
        },
        [toast, track],
    );

    return (
        <>
            {renderButton({
                isLoading: setupBilling.isPending,
                onAddPayment: handleAddPaymentMethod,
                showForm: !!showPaymentForm,
            })}
            {showPaymentForm && (
                <Dialog open onOpenChange={handleModalOpenChange}>
                    <DialogContent className="max-w-[700px]" data-track-location={TrackLocations.BillingModal}>
                        <DialogHeader>
                            <DialogTitle>{dialogTitle}</DialogTitle>
                            <DialogDescription>
                                Enter your payment details to complete the billing setup.
                            </DialogDescription>
                        </DialogHeader>
                        <DialogBody>
                            {isCompletingBillingSetup && (
                                <div className="mt-6 flex flex-row gap-4">
                                    <IconSpinner className="mb-4 animate-spin" size="lg" />
                                    <Text>Completing billing setup...</Text>
                                </div>
                            )}
                            {!isCompletingBillingSetup && (
                                <StripeWrapper clientSecret={showPaymentForm.clientSecret}>
                                    <StripePaymentForm
                                        clientSecret={showPaymentForm.clientSecret}
                                        onSuccess={handleSuccess}
                                        onError={handleError}
                                        data-tracking-id={dataTrackingId}
                                    />
                                </StripeWrapper>
                            )}
                        </DialogBody>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
};

const StripeWrapper = ({ clientSecret, children }: React.PropsWithChildren<{ clientSecret: string }>) => {
    const { stripePromise } = useStripePromise();

    if (!stripePromise) {
        return <LoadingState />;
    }

    return (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
            {children}
        </Elements>
    );
};
